/* eslint-disable max-lines -- Cohesive dev-only UI section (row, group, section). */
import { atom, useAtom } from 'jotai';
import { ChevronDown, RotateCcw, Sparkles } from 'lucide-react';
import {
  useDeferredValue,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useMutateMarkModalAsViewed } from '@/hooks/mutation/useMutateMarkModalAsViewed';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import {
  forceShowModalLocal,
  unseeAllModalsLocal,
  unseeModalLocal,
} from '@/lib/modal-debugger';
import { type ModalView } from '@/types';
import { Button } from '@heidi/design-system/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@heidi/design-system/dropdown-menu';
import { Label } from '@heidi/design-system/label';
import { SearchInput } from '@heidi/design-system/search-input';
import { Switch } from '@heidi/design-system/switch';
import {
  TypographyCaption,
  TypographyP2Bold,
  TypographyP2SemiBold,
} from '@heidi/design-system/typography';
import { ModalId } from '@heidi/schema/ml-scribe';

/**
 * ModalIds defined in the schema with no consumer in `src/` today.
 * Listed explicitly because we can't run a runtime grep — must be
 * pruned by hand when these IDs are removed from the BE schema or
 * picked up by a new feature.
 */
const DEAD_MODAL_IDS: readonly ModalId[] = [
  ModalId.DISCOVER_UPLOAD_SCHEDULE,
  ModalId.SHARE_SESSION_ANNOUNCE_SPOTLIGHT,
  ModalId.MENU_PIN_REMINDER,
];
const DEAD_MODAL_ID_SET: ReadonlySet<ModalId> = new Set(DEAD_MODAL_IDS);

const ALL_MODAL_IDS: readonly ModalId[] = [...Object.values(ModalId)].sort();

type GroupKey = 'active' | 'unseen' | 'dead';
const GROUP_ORDER: readonly GroupKey[] = ['active', 'unseen', 'dead'];
const GROUP_LABELS: Record<GroupKey, string> = {
  active: 'Active',
  unseen: 'Unseen',
  dead: 'Dead / unused',
};
// Only Active and Unseen are expanded by default — the Dead group is
// rarely useful and would otherwise dominate the visual hierarchy.
const DEFAULT_EXPANDED_GROUPS: readonly GroupKey[] = ['active', 'unseen'];

// In-memory atoms (no `atomWithStorage`) so filter + expansion state
// survive the dev sheet unmounting between opens within a session —
// the common loop is Force show → dismiss → tweak code → Force show
// again, and re-typing the search every cycle is the friction the
// sheet-close-on-Force-show change would otherwise introduce. A page
// reload deliberately resets these.
const seenModalsSearchAtom = atom('');
const seenModalsShowSeenOnlyAtom = atom(false);
const seenModalsExpandedGroupsAtom = atom<Set<GroupKey>>(
  new Set(DEFAULT_EXPANDED_GROUPS)
);

type ModalRow = {
  id: ModalId;
  view: ModalView | undefined;
};

const RELATIVE_TIME_FORMATTER = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

function formatRelativeTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return null;
  const diff = ms - Date.now();
  const absMin = Math.abs(diff) / 60_000;
  if (absMin < 1) return 'just now';
  if (absMin < 60)
    return RELATIVE_TIME_FORMATTER.format(Math.round(diff / 60_000), 'minute');
  const absHour = absMin / 60;
  if (absHour < 24)
    return RELATIVE_TIME_FORMATTER.format(Math.round(diff / 3_600_000), 'hour');
  return RELATIVE_TIME_FORMATTER.format(Math.round(diff / 86_400_000), 'day');
}

const ModalRowDisplay = ({
  id,
  view,
  onRequestClose,
}: ModalRow & { onRequestClose?: () => void }) => {
  const { mutate: markModalAsViewed } = useMutateMarkModalAsViewed();
  const lastSeen = formatRelativeTime(view?.last_seen_at);
  const hideForever = view?.hide_forever ?? false;

  return (
    <div className="flex flex-col gap-1.5">
      <TypographyP2Bold className="break-all font-mono text-text-primary">
        {id}
      </TypographyP2Bold>
      {view ? (
        <TypographyCaption className="text-text-secondary">
          <span className="font-mono tabular-nums">
            v:{view.view_count} · d:{view.consecutive_dismiss_count ?? 0} · e:
            {view.engage_count ?? 0} · hide:{String(hideForever)}
          </span>
          {lastSeen ? <> · last seen {lastSeen}</> : null}
        </TypographyCaption>
      ) : (
        <TypographyCaption className="text-text-tertiary">
          No record. Treated as unseen.
        </TypographyCaption>
      )}
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          size="xs"
          onClick={() => unseeModalLocal(id)}
          disabled={!view}
          data-testid={`dev-tools-seen-modals-reset-${id}`}
        >
          <RotateCcw size={12} aria-hidden /> Reset
        </Button>
        <Button
          type="button"
          variant="outline"
          size="xs"
          // `increment_count: false` keeps the BE legacy path from bumping
          // view_count just because we toggled hide_forever — the developer
          // hasn't actually seen the modal again.
          onClick={() =>
            markModalAsViewed({
              modal_id: id,
              increment_count: false,
              hide_forever: !hideForever,
            })
          }
          data-testid={`dev-tools-seen-modals-toggle-hide-forever-${id}`}
        >
          {hideForever ? 'Clear hide_forever' : 'Set hide_forever'}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="xs"
          // Close the dev sheet after forcing the modal — the modal renders
          // beneath the sheet (z-[10000]) and any interaction with the sheet
          // dismisses it, so leaving the sheet open makes Force show
          // unusable in practice. Mirrors the `onRequestClose` pattern in
          // `EvidenceQuotaToastDevSection`.
          onClick={() => {
            forceShowModalLocal(id);
            onRequestClose?.();
          }}
          data-testid={`dev-tools-seen-modals-force-show-${id}`}
        >
          <Sparkles size={12} aria-hidden /> Force show
        </Button>
      </div>
    </div>
  );
};

const ModalGroup = ({
  groupKey,
  rows,
  isFiltering,
  isExpanded,
  onToggle,
  setRef,
  stickyTopPx,
  onRequestClose,
}: {
  groupKey: GroupKey;
  rows: ModalRow[];
  isFiltering: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  setRef: (el: HTMLDivElement | null) => void;
  stickyTopPx: number;
  onRequestClose?: () => void;
}) => {
  if (rows.length === 0 && isFiltering) return null;
  return (
    <div
      ref={setRef}
      className="flex flex-col gap-3"
      style={{ scrollMarginTop: stickyTopPx }}
    >
      {/* Native <button> as the accordion-trigger primitive — same rationale as `feature-flags-dev-section`'s FlagGroup: making the DS Button work as a sticky full-width row would require disableAutomaticTypographyClasses (discouraged) plus many className overrides. */}
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isExpanded}
        className="sticky z-10 -mr-2 flex w-full items-center justify-between gap-2 rounded-sm bg-surface-elevated py-1 pr-2 text-left focus-visible:outline-1 focus-visible:outline-outline-focus"
        style={{ top: stickyTopPx }}
        data-testid={`dev-tools-seen-modals-toggle-${groupKey}`}
      >
        <TypographyP2Bold>
          {GROUP_LABELS[groupKey]} ({rows.length})
        </TypographyP2Bold>
        <ChevronDown
          size={14}
          className={`shrink-0 transition-transform ${isExpanded ? '' : '-rotate-90'}`}
          aria-hidden
        />
      </button>
      {isExpanded && (
        <div className="flex flex-col gap-7">
          {rows.map((row) => (
            <ModalRowDisplay key={row.id} {...row} onRequestClose={onRequestClose} />
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Dev-tools section that lists every `ModalId` with its current
 * `seen_modals` record from `useQueryUser`, and lets a developer:
 *   - Reset (optimistic-only): clears the local cache + atoms so the
 *     modal behaves as if unseen until the next user refetch.
 *   - Toggle `hide_forever`: BE-durable round-trip via
 *     `useMutateMarkModalAsViewed`.
 *   - Force show: inject the modal into the user-triggered queue at
 *     CRITICAL priority (delegates to `__heidiModals.forceShow`).
 */
export const SeenModalsDevSection = ({
  onRequestClose,
}: {
  /** Called after the user triggers an action that should close the dev-tools sheet. */
  onRequestClose?: () => void;
}) => {
  const { data: seenModals } = useQueryUser((data) => data?.seen_modals);

  const [search, setSearch] = useAtom(seenModalsSearchAtom);
  const deferredSearch = useDeferredValue(search);
  const [showSeenOnly, setShowSeenOnly] = useAtom(seenModalsShowSeenOnlyAtom);

  const allRows: ModalRow[] = useMemo(
    () =>
      ALL_MODAL_IDS.map((id) => ({
        id,
        view: seenModals?.[id],
      })),
    [seenModals]
  );

  const seenCount = useMemo(
    () => allRows.reduce((c, r) => (r.view ? c + 1 : c), 0),
    [allRows]
  );

  const filteredRows = useMemo(() => {
    const needle = deferredSearch.trim().toLowerCase();
    return allRows.filter((row) => {
      if (showSeenOnly && !row.view) return false;
      if (!needle) return true;
      return row.id.toLowerCase().includes(needle);
    });
  }, [allRows, deferredSearch, showSeenOnly]);

  const groupedRows: Record<GroupKey, ModalRow[]> = useMemo(() => {
    const groups: Record<GroupKey, ModalRow[]> = { active: [], unseen: [], dead: [] };
    for (const row of filteredRows) {
      if (DEAD_MODAL_ID_SET.has(row.id)) groups.dead.push(row);
      else if (row.view) groups.active.push(row);
      else groups.unseen.push(row);
    }
    return groups;
  }, [filteredRows]);

  // Auto-clear the "Show seen only" filter when there are no seen
  // modals so the user isn't left staring at an empty list (mirrors
  // the flag-tab's "Show changed only" auto-clear).
  useEffect(() => {
    if (seenCount === 0 && showSeenOnly) setShowSeenOnly(false);
  }, [seenCount, showSeenOnly, setShowSeenOnly]);

  const stickyHeaderRef = useRef<HTMLDivElement>(null);
  const [stickyHeaderHeight, setStickyHeaderHeight] = useState(0);
  useLayoutEffect(() => {
    const el = stickyHeaderRef.current;
    if (!el) return;
    const update = () => setStickyHeaderHeight(el.getBoundingClientRect().height);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const [expanded, setExpanded] = useAtom(seenModalsExpandedGroupsAtom);
  const isFiltering = deferredSearch.trim().length > 0 || showSeenOnly;
  const groupRefs = useRef<Record<GroupKey, HTMLDivElement | null>>({
    active: null,
    unseen: null,
    dead: null,
  });
  const toggleGroup = (group: GroupKey) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  };

  return (
    <section aria-labelledby="dev-tools-seen-modals-heading">
      <div
        ref={stickyHeaderRef}
        className="sticky top-0 z-20 -mr-2 bg-surface-elevated pb-3 pr-2 pt-1"
      >
        <div className="mb-2 flex items-center justify-between gap-2">
          <TypographyP2SemiBold id="dev-tools-seen-modals-heading">
            Seen modals
          </TypographyP2SemiBold>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="xs"
                data-testid="dev-tools-seen-modals-bulk-actions"
              >
                Bulk actions{seenCount > 0 ? ` (${seenCount})` : ''}
                <ChevronDown size={14} aria-hidden />
              </Button>
            </DropdownMenuTrigger>
            {/* z-[10001] sits one above the dev sheet (z-[10000]) so the dropdown isn't clipped. Same dev-only override the flag tab uses. */}
            <DropdownMenuContent align="end" className="z-[10001]">
              <DropdownMenuItem
                onSelect={() => unseeAllModalsLocal()}
                disabled={seenCount === 0}
                data-testid="dev-tools-seen-modals-reset-all"
              >
                Reset all{seenCount > 0 ? ` (${seenCount})` : ''}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <TypographyCaption className="mb-3 text-text-tertiary">
          Reset / Force show are optimistic-only — survive until the next user refetch
          (window focus). hide_forever toggle persists via POST /user/modal-view.
        </TypographyCaption>
        <SearchInput
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search modals by id"
          aria-label="Search seen modals"
          data-testid="dev-tools-seen-modals-search"
        />
        <Label className="mt-2 flex cursor-pointer select-none items-center gap-2">
          <Switch
            size="xs"
            checked={showSeenOnly}
            onCheckedChange={setShowSeenOnly}
            disabled={seenCount === 0}
            data-testid="dev-tools-seen-modals-seen-only"
          />
          <TypographyCaption className="text-text-secondary">
            Show seen only{seenCount > 0 ? ` (${seenCount})` : ''}
          </TypographyCaption>
        </Label>
      </div>
      {filteredRows.length === 0 ? (
        <TypographyCaption className="text-text-tertiary">
          {showSeenOnly && deferredSearch.trim()
            ? `No seen modals match “${deferredSearch.trim()}”.`
            : showSeenOnly
              ? 'No seen modals.'
              : `No modals match “${deferredSearch.trim()}”.`}
        </TypographyCaption>
      ) : (
        <div className="flex flex-col gap-5">
          {GROUP_ORDER.map((groupKey) => (
            <ModalGroup
              key={groupKey}
              groupKey={groupKey}
              rows={groupedRows[groupKey]}
              isFiltering={isFiltering}
              isExpanded={isFiltering || expanded.has(groupKey)}
              onToggle={() => toggleGroup(groupKey)}
              setRef={(el) => {
                groupRefs.current[groupKey] = el;
              }}
              stickyTopPx={stickyHeaderHeight}
              onRequestClose={onRequestClose}
            />
          ))}
        </div>
      )}
    </section>
  );
};
